import { escapeHtml, stripHtmlTags, sanitizeHtmlContent } from './html-sanitization';

export interface ValidationResult {
  isValid: boolean;
  error?: string;
  warnings?: string[];
  sanitizedValue?: string;
}

export interface ValidationOptions {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  allowHtml?: boolean;
  trimWhitespace?: boolean;
  customPattern?: RegExp;
  customValidator?: (value: string) => ValidationResult;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const COMMON_EMAIL_TYPOS = [
  { typo: '@gmail.co', correct: '@gmail.com' },
  { typo: '@gmial.com', correct: '@gmail.com' },
  { typo: '@gamil.com', correct: '@gmail.com' },
  { typo: '@gmal.com', correct: '@gmail.com' },
  { typo: '@gnail.com', correct: '@gmail.com' },
  { typo: '@yahoo.co', correct: '@yahoo.com' },
  { typo: '@yaho.com', correct: '@yahoo.com' },
  { typo: '@hotmail.co', correct: '@hotmail.com' },
  { typo: '@hotmal.com', correct: '@hotmail.com' },
  { typo: '@hotnail.com', correct: '@hotmail.com' },
  { typo: '@outlook.co', correct: '@outlook.com' },
  { typo: '@outlok.com', correct: '@outlook.com' },
  { typo: '@icloud.co', correct: '@icloud.com' },
  { typo: '@icoud.com', correct: '@icloud.com' },
  { typo: '.con', correct: '.com' },
  { typo: '.cpm', correct: '.com' },
  { typo: '.vom', correct: '.com' },
  { typo: '.comm', correct: '.com' },
  { typo: '@@', correct: '@' },
  { typo: '..', correct: '.' },
];

export const validateEmail = (email: string): ValidationResult => {
  if (!email || typeof email !== 'string') return { isValid: false, error: 'Email is required' };

  const trimmedEmail = email.trim().toLowerCase();
  if (!EMAIL_REGEX.test(trimmedEmail)) return { isValid: false, error: 'Please enter a valid email address' };
  if (trimmedEmail.length > 254) return { isValid: false, error: 'Email address is too long' };

  const warnings = COMMON_EMAIL_TYPOS
    .filter(({ typo }) => trimmedEmail.includes(typo))
    .map(({ correct }) => `Did you mean ${correct}?`);

  return { isValid: true, sanitizedValue: trimmedEmail, warnings: warnings.length > 0 ? warnings : undefined };
};

const SQL_INJECTION_REGEX = /(\b(SELECT|INSERT|UPDATE|DELETE|DROP|CREATE|ALTER|EXEC|UNION|SCRIPT)\b)/gi;

export const sanitizeString = (input: string, options: ValidationOptions = {}): ValidationResult => {
  if (!input || typeof input !== 'string') {
    return options.required ? { isValid: false, error: 'This field is required' } : { isValid: true, sanitizedValue: '' };
  }

  let sanitized = options.trimWhitespace !== false ? input.trim() : input;

  if (options.minLength && sanitized.length < options.minLength) {
    return { isValid: false, error: `Must be at least ${options.minLength} characters long` };
  }
  if (options.maxLength && sanitized.length > options.maxLength) {
    return { isValid: false, error: `Must be no more than ${options.maxLength} characters long` };
  }
  if (!options.allowHtml) {
    sanitized = escapeHtml(stripHtmlTags(sanitized));
  }
  if (SQL_INJECTION_REGEX.test(sanitized)) {
    return { isValid: false, error: 'Input contains potentially dangerous content' };
  }
  if (options.customPattern && !options.customPattern.test(sanitized)) {
    return { isValid: false, error: 'Input does not match the required format' };
  }
  if (options.customValidator) {
    const customResult = options.customValidator(sanitized);
    if (!customResult.isValid) return customResult;
  }

  return { isValid: true, sanitizedValue: sanitized };
};

export const sanitizeEmployeeName = (name: string): ValidationResult => sanitizeString(name, {
  required: true, minLength: 2, maxLength: 50, allowHtml: false, customPattern: /^[a-zA-Z\s\-'.]+$/
});

export const sanitizeShiftTitle = (title: string): ValidationResult => sanitizeString(title, {
  required: false, maxLength: 100, allowHtml: false
});

export const sanitizeSearchQuery = (query: string): ValidationResult => sanitizeString(query, {
  required: false, maxLength: 200, allowHtml: false, trimWhitespace: true
});

export const sanitizeMessageContent = (content: string): ValidationResult => {
  if (!content || typeof content !== 'string') return { isValid: false, error: 'Message content is required' };
  const trimmed = content.trim();
  if (trimmed.length > 5000) return { isValid: false, error: 'Message is too long' };
  return { isValid: true, sanitizedValue: sanitizeHtmlContent(trimmed) };
};

export const validateNumber = (
  value: string | number,
  options: { min?: number; max?: number; required?: boolean; integer?: boolean; positive?: boolean } = {}
): ValidationResult => {
  if (value === '' || value === null || value === undefined) {
    return options.required ? { isValid: false, error: 'This field is required' } : { isValid: true, sanitizedValue: undefined };
  }

  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return { isValid: false, error: 'Must be a valid number' };
  if (options.integer && !Number.isInteger(num)) return { isValid: false, error: 'Must be a whole number' };
  if (options.positive && num <= 0) return { isValid: false, error: 'Must be a positive number' };
  if (options.min !== undefined && num < options.min) return { isValid: false, error: `Must be at least ${options.min}` };
  if (options.max !== undefined && num > options.max) return { isValid: false, error: `Must be no more than ${options.max}` };

  return { isValid: true, sanitizedValue: num };
};

export const validateDuration = (minutes: string | number): ValidationResult =>
  validateNumber(minutes, { min: 1, max: 24 * 60, integer: true, positive: true });

export const validateHourlyRate = (rate: string | number): ValidationResult =>
  validateNumber(rate, { min: 0.01, max: 1000, positive: true });

export const validateUrl = (url: string, options: { allowHttp?: boolean } = {}): ValidationResult => {
  if (!url || typeof url !== 'string') return { isValid: false, error: 'URL is required' };

  try {
    const parsedUrl = new URL(url.trim());
    if (!options.allowHttp && parsedUrl.protocol === 'http:') return { isValid: false, error: 'Only secure HTTPS URLs are allowed' };
    if (!['http:', 'https:'].includes(parsedUrl.protocol)) return { isValid: false, error: 'Only HTTP and HTTPS URLs are allowed' };
    if (parsedUrl.href.includes('javascript:') || parsedUrl.href.includes('data:')) {
      return { isValid: false, error: 'URL contains potentially dangerous content' };
    }
    return { isValid: true, sanitizedValue: parsedUrl.href };
  } catch {
    return { isValid: false, error: 'Please enter a valid URL' };
  }
};

export const validatePhoneNumber = (phone: string): ValidationResult => {
  if (!phone || typeof phone !== 'string') return { isValid: false, error: 'Phone number is required' };
  const cleaned = phone.replace(/[\s\-\(\)\+\.]/g, '');
  if (!/^\+?\d{7,15}$/.test(cleaned)) return { isValid: false, error: 'Please enter a valid phone number (7-15 digits)' };
  return { isValid: true, sanitizedValue: cleaned };
};

export const validateFields = (
  fields: Record<string, { value: string; validator: (value: string) => ValidationResult }>
): { isValid: boolean; errors: Record<string, string>; sanitizedValues: Record<string, any> } => {
  const errors: Record<string, string> = {};
  const sanitizedValues: Record<string, any> = {};
  let isValid = true;

  Object.entries(fields).forEach(([key, { value, validator }]) => {
    const result = validator(value);
    if (!result.isValid) {
      errors[key] = result.error || 'Invalid value';
      isValid = false;
    } else {
      sanitizedValues[key] = result.sanitizedValue ?? value;
    }
  });

  return { isValid, errors, sanitizedValues };
};

export const validateInput = sanitizeString;